'use strict';

/**
 * Main Product V1 Section JavaScript
 * Custom Elements for gallery, plan CTA, accordion, and selling plans
 */

/* ==========================================================================
   Gallery Component
   ========================================================================== */
if (!customElements.get('mpv1-gallery')) {
  class Mpv1Gallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.thumbnails = [];
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
  this.radios       = Array.from(this.querySelectorAll('.mpv1-rpp__radio'));
  this.cards        = Array.from(this.querySelectorAll('.mpv1-rpp__card'));
  this.atcBtn       = this.querySelector('.mpv1-rpp__atc-btn');
  this.priceText    = this.querySelector('.mpv1-rpp__atc-price-text');
  this.compareEl    = this.querySelector('.mpv1-rpp__atc-compare');
  this.barPrice     = this.querySelector('.mpv1-rpp__price-bar-price');
  this.barCompare   = this.querySelector('.mpv1-rpp__price-bar-compare');
  this.barBadge     = this.querySelector('.mpv1-rpp__price-bar-badge');
  this.errorEl      = this.querySelector('.mpv1-rpp__error');
  this.mainImage = this.querySelector('.mpv1-gallery__main-img');
  this.thumbnails = Array.from(this.querySelectorAll('.mpv1-gallery__thumb'));
  this.prevBtn = this.querySelector('[data-gallery-prev]');
  this.nextBtn = this.querySelector('[data-gallery-next]');
  this.mainWrapper = this.querySelector('.mpv1-gallery__main');
  this.thumbsContainer = this.querySelector('.mpv1-gallery__thumbs');
  
if (!this.mainImage || this.thumbnails.length === 0) return;

      // Find initial active thumbnail
      this.currentIndex = this.thumbnails.findIndex(thumb => thumb.classList.contains('is-active'));
      if (this.currentIndex === -1) this.currentIndex = 0;

      this.bindEvents();
      this.updateNavButtons();


  if (this.radios.length === 0) return;

  const defaultCard = this.querySelector('[data-default] .mpv1-rpp__radio');

  if (defaultCard) {
    defaultCard.checked = true;
  } else if (!this.querySelector('.mpv1-rpp__radio:checked') && this.radios[0]) {
    this.radios[0].checked = true;
  }

  this.cards.forEach(card => {
    card.addEventListener('click', () => {
      const radio = card.querySelector('.mpv1-rpp__radio');

      if (!radio) return;

      radio.checked = true;
      this._updateSelection();
    });
  });

  this.radios.forEach(radio => {
    radio.addEventListener('change', () => {
      radio.checked = true;
      this._updateSelection();
    });
  });

  this._updateSelection();

  if (this.atcBtn) {
    this.atcBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this._handleSubscriptionAddToCart();
    }, true);
  }
}

    bindEvents() {
      // Thumbnail clicks
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goToSlide(index));
      });

      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Keyboard navigation
      this.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Touch/swipe support on main image wrapper
      if (this.mainWrapper) {
        this.mainWrapper.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        this.mainWrapper.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
      }
    }

    handleKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }

    handleSwipe() {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) < swipeThreshold) return;

      if (diff > 0) {
        // Swipe left - go next
        this.next();
      } else {
        // Swipe right - go prev
        this.prev();
      }
    }

    prev() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    next() {
      if (this.currentIndex < this.thumbnails.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      }
    }

    goToSlide(index) {
      if (index < 0 || index >= this.thumbnails.length) return;

      const thumb = this.thumbnails[index];
      const fullSrc = thumb.dataset.full;
      const srcset = thumb.dataset.srcset;
      const alt = thumb.dataset.alt || '';

      if (!fullSrc) return;

      // Update main image — set srcset before src to avoid a double network
      // request: if src fires first the browser starts fetching the 1200px
      // image, then cancels/restarts when srcset is evaluated a tick later.
      this.mainImage.classList.add('is-loading');
      const onLoad = () => {
        this.mainImage.classList.remove('is-loading');
        this.mainImage.removeEventListener('load', onLoad);
      };
      this.mainImage.addEventListener('load', onLoad);

      if (srcset) {
        this.mainImage.srcset = srcset;
      }
      this.mainImage.src = fullSrc;
      this.mainImage.alt = alt;

      // Update active states
      this.thumbnails.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      this.currentIndex = index;
      this.updateNavButtons();

      // Scroll active thumbnail into view — scroll the strip container directly
      // to avoid scrollIntoView bubbling up to the viewport and shifting the layout.
      if (this.thumbsContainer) {
        const scrollLeft = thumb.offsetLeft - (this.thumbsContainer.offsetWidth / 2) + (thumb.offsetWidth / 2);
        this.thumbsContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }

    updateNavButtons() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
        this.prevBtn.setAttribute('aria-disabled', this.currentIndex === 0 ? 'true' : 'false');
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentIndex === this.thumbnails.length - 1;
        this.nextBtn.setAttribute('aria-disabled', this.currentIndex === this.thumbnails.length - 1 ? 'true' : 'false');
      }
    }
  }

  customElements.define('mpv1-gallery', Mpv1Gallery);
}

/* ==========================================================================
   Pack Selector Component
   Quantity-based bundle picker — single variant, no selling plans.
   Each card has its own ORDER NOW button. Clicking it directly adds that
   plan's quantity to cart and redirects to checkout.
   Passes supply_plan + rebill_days as line item properties so the
   external subscription platform (Phoenix / Checkout Champ) can read
   them at checkout.
   ========================================================================== */
if (!customElements.get('mpv1-pack-selector')) {
  class Mpv1PackSelector extends HTMLElement {
    constructor() {
      super();
      this.loadingBtn = null;
      this.errorEl = null;
      this.layoutType = null;
    }

    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.layoutType = this.dataset.layout || 'cards';
      this.errorEl = this.querySelector('.mpv1-pack__error');

      if (!this.variantId) return;

      if (this.layoutType === 'switchers') {
        this._initSwitchers();
      } else {
        this._initCards();
      }
    }

    _initCards() {
      this.cards = Array.from(this.querySelectorAll('.mpv1-pack'));
      if (this.cards.length === 0) return;

      // Each card has its own CTA button — bind per-card
      this.cards.forEach(card => {
        const btn = card.querySelector('.mpv1-pack__cta-btn');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._handleAddToCartFromCard(card, btn);
          });
        }
      });
    }

    _initSwitchers() {
      this.radios = Array.from(this.querySelectorAll('.mpv1-switcher__radio'));
      this.switcherBtn = this.querySelector('.mpv1-switchers__cta-btn');
      this.switcherBtnText = this.querySelector('.mpv1-switchers__cta-text');
      this.baseButtonText = this.switcherBtnText ? this.switcherBtnText.textContent.trim() : 'ADD TO CART';
      this.onetimeLink  = this.querySelector('[data-onetime-link]');
      this.onetimePrice = this.querySelector('.mpv1-switcher__onetime-price');

      if (this.radios.length === 0 || !this.switcherBtn) return;

      // Select default option
      const defaultOption = this.querySelector('[data-default] .mpv1-switcher__radio');
      if (defaultOption) {
        defaultOption.checked = true;
      } else if (this.radios[0]) {
        this.radios[0].checked = true;
      }

      // Set initial button text and href
      this._updateSwitcherSelection();

      // Update selection state on radio change
    
    }

    _updateSwitcherSelection() {
      if (!this.switcherBtnText) return;

      // Find selected radio and get the total price from parent label
      const selectedRadio = this.querySelector('.mpv1-switcher__radio:checked');
      if (!selectedRadio) return;

      const label = selectedRadio.closest('label');
      const totalPrice = label ? label.dataset.totalPrice : '';
      const checkoutUrl = label ? label.dataset.checkoutUrl : '';

      // Update button text to show: "ADD TO CART - $149.70"
      if (totalPrice) {
        this.switcherBtnText.textContent = `${this.baseButtonText} - ${totalPrice}`;
      } else {
        this.switcherBtnText.textContent = this.baseButtonText;
      }

      // Update anchor href to checkout URL from selected option
      if (this.switcherBtn && checkoutUrl) {
        this.switcherBtn.href = checkoutUrl;
      }

      // Update one-time purchase link: price and href reflect the selected plan
      if (label) {
        const otPrice = label.dataset.onetimePrice;
        const otUrl   = label.dataset.onetimeUrl;
        if (this.onetimePrice && otPrice) {
          this.onetimePrice.textContent = otPrice;
        }
        if (this.onetimeLink && otUrl) {
          this.onetimeLink.href = otUrl;
        }
      }
    }

    async _handleAddToCartFromCard(card, btn) {
      if (btn.classList.contains('is-loading')) return;

      this._clearError();
      this._setLoading(btn, true);

      const qty        = parseInt(card.dataset.qty, 10) || 1;
      const supplyPlan = card.dataset.supplyPlan || '';
      const rebillDays = card.dataset.rebillDays || '';

      await this._addToCart(qty, supplyPlan, rebillDays, btn);
    }

    async _handleAddToCartFromSwitcher(btn) {
      if (btn.classList.contains('is-loading')) return;

      // Find selected radio
      const selectedRadio = this.querySelector('.mpv1-switcher__radio:checked');
      if (!selectedRadio) {
        this._showError('Please select a supply option');
        return;
      }

      this._clearError();
      this._setLoading(btn, true);

      // Get data from parent label
      const label = selectedRadio.closest('label');
      const qty        = parseInt(label.dataset.qty, 10) || 1;
      const supplyPlan = label.dataset.supplyPlan || '';
      const rebillDays = label.dataset.rebillDays || '';

      await this._addToCart(qty, supplyPlan, rebillDays, btn);
    }

    async _addToCart(qty, supplyPlan, rebillDays, btn) {
      const cartItem = {
        id: parseInt(this.variantId, 10),
        quantity: qty,
        properties: {}
      };

      // Only attach properties that have values so the cart stays clean
      if (supplyPlan) cartItem.properties['_supply_plan'] = supplyPlan;
      if (rebillDays) cartItem.properties['_rebill_days'] = rebillDays;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: [cartItem] })
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description || 'Could not add to cart');
        }

        const data = await response.json();

        // Notify GTM / GA4 listeners
        document.dispatchEvent(new CustomEvent('cart:item:added', {
          bubbles: true,
          detail: {
            items: data.items || data,
            variantId: cartItem.id,
            quantity: qty,
            supplyPlan,
            rebillDays
          }
        }));

        this._setLoading(btn, false);

       window.updateCartDrawer();
      } catch (err) {
        console.error('[mpv1-pack-selector] Add to cart error:', err);
        this._setLoading(btn, false);
        this._showError(err.message || 'Something went wrong. Please try again.');
      }
    }

    _setLoading(btn, loading) {
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.loadingBtn = loading ? btn : null;
    }

    _showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    _clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  customElements.define('mpv1-pack-selector', Mpv1PackSelector);
}

/* ==========================================================================
   Accordion Component
   ========================================================================== */
if (!customElements.get('mpv1-accordion')) {
  class Mpv1Accordion extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
    }

   connectedCallback() {
  this.radios       = Array.from(this.querySelectorAll('.mpv1-rpp__radio'));
  this.atcBtn       = this.querySelector('.mpv1-rpp__atc-btn');
  this.priceText    = this.querySelector('.mpv1-rpp__atc-price-text');
  this.compareEl    = this.querySelector('.mpv1-rpp__atc-compare');
  this.barPrice     = this.querySelector('.mpv1-rpp__price-bar-price');
  this.barCompare   = this.querySelector('.mpv1-rpp__price-bar-compare');
  this.barBadge     = this.querySelector('.mpv1-rpp__price-bar-badge');
  this.errorEl      = this.querySelector('.mpv1-rpp__error');

  if (this.radios.length === 0) return;

  const defaultCard = this.querySelector('[data-default] .mpv1-rpp__radio');
  if (defaultCard && !this.querySelector('.mpv1-rpp__radio:checked')) {
    defaultCard.checked = true;
  }

  this._updateSelection();

  this.radios.forEach(radio => {
    radio.addEventListener('change', () => this._updateSelection());
  });

  if (this.atcBtn) {
    this.atcBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this._handleSubscriptionAddToCart();
    }, true);
  }
}

    generateId() {
      return Math.random().toString(36).substring(2, 9);
    }

    toggle() {
      this.isOpen = !this.isOpen;
      this.classList.toggle('is-open', this.isOpen);
      this.trigger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

      if (this.content) {
        if (this.isOpen) {
          // Measure actual content height before opening so the CSS transition
          // animates to the correct pixel value (P1 fix: stale height on close).
          const height = this.content.scrollHeight;
          this.style.setProperty('--mpv1-accordion-height', `${height}px`);
        } else {
          // Reset the height var on close so a subsequent open re-measures correctly.
          this.style.removeProperty('--mpv1-accordion-height');
        }
      }
    }

    open() {
      if (!this.isOpen) this.toggle();
    }

    close() {
      if (this.isOpen) this.toggle();
    }
  }

  customElements.define('mpv1-accordion', Mpv1Accordion);
}

/* ==========================================================================
   Resilia Pack Selector Component
   Resilia-style plan picker: horizontal card rows with radio selection.
   Clicking a card updates the ATC button price text and checkout URL.
   ========================================================================== */
/* ==========================================================================
   Resilia Pack Selector Component
   ========================================================================== */
if (!customElements.get('mpv1-pack-selector-resilia')) {
  class Mpv1PackSelectorResilia extends HTMLElement {
    connectedCallback() {
      this.radios       = Array.from(this.querySelectorAll('.mpv1-rpp__radio'));
      this.atcBtn       = this.querySelector('.mpv1-rpp__atc-btn');
      this.priceText    = this.querySelector('.mpv1-rpp__atc-price-text');
      this.compareEl    = this.querySelector('.mpv1-rpp__atc-compare');
      this.barPrice     = this.querySelector('.mpv1-rpp__price-bar-price');
      this.barCompare   = this.querySelector('.mpv1-rpp__price-bar-compare');
      this.barBadge     = this.querySelector('.mpv1-rpp__price-bar-badge');
      this.errorEl      = this.querySelector('.mpv1-rpp__error');

      this._setSubscriptionCheckboxDefaultChecked();

      if (this.radios.length === 0) return;

      this.cards = this.radios
        .map((radio) => this._getCardFromRadio(radio))
        .filter(Boolean);

      const defaultRadio = this.querySelector('[data-default] .mpv1-rpp__radio');

      if (defaultRadio) {
        defaultRadio.checked = true;
      } else if (!this.querySelector('.mpv1-rpp__radio:checked') && this.radios[0]) {
        this.radios[0].checked = true;
      }

      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          radio.checked = true;
          this._updateSelection();
        });
      });

      this.cards.forEach((card) => {
        card.addEventListener('click', (event) => {
          if (event.target.closest('.mpv1-rpp__atc-btn')) return;

          const radio = card.querySelector('.mpv1-rpp__radio');
          if (!radio) return;

          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      const subscriptionToggle = this.querySelector('[data-mpv1-subscription-toggle]');
      if (subscriptionToggle) {
        subscriptionToggle.addEventListener('change', () => {
          this._updateSelection();
        });
      }

      this._updateSelection();

      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          this._handleSubscriptionAddToCart();
        }, true);
      }
    }

    _setSubscriptionCheckboxDefaultChecked() {
      const subscriptionToggle = this.querySelector('[data-mpv1-subscription-toggle]');

      if (!subscriptionToggle) return;

      // Default subscription checkbox unchecked on page load.
      // User can manually check it before clicking Add To Cart.
      subscriptionToggle.checked = false;
      subscriptionToggle.defaultChecked = false;
      subscriptionToggle.removeAttribute('checked');
      subscriptionToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _getCardFromRadio(radio) {
      return (
        radio.closest('[data-checkout-url]') ||
        radio.closest('.mpv1-rpp__card') ||
        radio.closest('label')
      );
    }

    _getDisplayPricingForCard(card) {
      const baseTotalPrice = card ? (card.dataset.totalPrice || '') : '';
      const baseComparePrice = card ? (card.dataset.comparePrice || '') : '';

      // Only copy subscription PDP gets visual 20% price reduction.
      // Compare price remains exactly the same.
      if (!this._isCopySubscriptionPdp() || !this._wantsCopySubscription()) {
        return {
          totalPrice: baseTotalPrice,
          comparePrice: baseComparePrice
        };
      }

      const discountedTotalPrice = this._getDiscountedMoney(baseTotalPrice, 5);

      return {
        totalPrice: discountedTotalPrice || baseTotalPrice,
        comparePrice: baseComparePrice
      };
    }

    _getDiscountedMoney(priceStr, discountPercent) {
      if (!priceStr) return '';

      const priceNumber = this._parseMoney(priceStr);
      if (!priceNumber || priceNumber <= 0) return '';

      const discountedAmount = priceNumber * (1 - (discountPercent / 100));

      return this._formatMoney(discountedAmount, priceStr);
    }

    _updateCardDisplayPrices() {
      if (!this.cards || this.cards.length === 0) return;

      this.cards.forEach((card) => {
        const displayPricing = this._getDisplayPricingForCard(card);

        const priceEl = card.querySelector('.mpv1-rpp__price');
        if (priceEl && displayPricing.totalPrice) {
          priceEl.textContent = displayPricing.totalPrice;
        }

        const compareEl =
          card.querySelector('.mpv1-rpp__compare') ||
          card.querySelector('.mpv1-rpp__pricing s');

        if (compareEl) {
          if (displayPricing.comparePrice) {
            compareEl.textContent = displayPricing.comparePrice;
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
      });
    }

    _updateSelection() {
      const checked = this.querySelector('.mpv1-rpp__radio:checked');
      if (!checked) return;

      const card = this._getCardFromRadio(checked);
      if (!card) return;

      this.cards.forEach((item) => {
        item.classList.toggle('is-selected', item === card);
      });

      this._updateCardDisplayPrices();

      const displayPricing = this._getDisplayPricingForCard(card);
      const totalPrice   = displayPricing.totalPrice || '';
      const comparePrice = displayPricing.comparePrice || '';
      const checkoutUrl  = card.dataset.checkoutUrl || '#';

      if (this.priceText) {
        this.priceText.textContent = totalPrice ? ` | ${totalPrice}` : '';
      }

      if (this.compareEl) {
        if (comparePrice) {
          this.compareEl.textContent = comparePrice;
          this.compareEl.hidden = false;
        } else {
          this.compareEl.textContent = '';
          this.compareEl.hidden = true;
        }
      }

      if (this.atcBtn) {
        this.atcBtn.href = 'javascript:void(0)';
        this.atcBtn.dataset.selectedCheckoutUrl = checkoutUrl;

        const btnLabel =
          this.atcBtn.querySelector('.mpv1-rpp__atc-label')?.textContent?.trim() ||
          'Add To Cart';

        this.atcBtn.setAttribute(
          'aria-label',
          totalPrice ? `${btnLabel} – ${totalPrice}` : btnLabel
        );
      }

      if (this.barPrice && totalPrice) {
        this.barPrice.textContent = totalPrice;
      }

      if (this.barCompare) {
        if (comparePrice) {
          this.barCompare.textContent = comparePrice;
          this.barCompare.hidden = false;
        } else {
          this.barCompare.textContent = '';
          this.barCompare.hidden = true;
        }
      }

      if (this.barBadge) {
        if (comparePrice && totalPrice) {
          const savings = this._parseMoney(comparePrice) - this._parseMoney(totalPrice);

          if (savings > 0) {
            const prefix = (this.barBadge.dataset.prefix || '').trim();
            this.barBadge.textContent = prefix
              ? `${prefix} ${this._formatMoney(savings, totalPrice)}`
              : this._formatMoney(savings, totalPrice);
            this.barBadge.hidden = false;
          } else {
            this.barBadge.hidden = true;
          }
        } else {
          this.barBadge.hidden = true;
        }
      }
    }

    _isCopySubscriptionPdp() {
      return this.getAttribute('data-is-copy-subscription-pdp') === 'true';
    }

    _getSelectedPlanIndex() {
      const selectedRadio = this.querySelector('.mpv1-rpp__radio:checked');

      if (selectedRadio) {
        const radioValue = parseInt(selectedRadio.value, 10);

        if (radioValue >= 1 && radioValue <= 3) {
          return radioValue;
        }

        const card = this._getCardFromRadio(selectedRadio);
        const wrap = card ? card.closest('.mpv1-rpp__card-wrap') : null;
        const planValue = wrap ? parseInt(wrap.dataset.plan, 10) : null;

        if (planValue >= 1 && planValue <= 3) {
          return planValue;
        }
      }

      return 1;
    }

    _getSafeId(value) {
      const match = String(value || '').trim().match(/\d{6,}/);
      if (!match) return null;

      const id = parseInt(match[0], 10);
      return Number.isFinite(id) && id > 0 ? id : null;
    }

    _wantsCopySubscription() {
      const subscriptionToggle = this.querySelector('[data-mpv1-subscription-toggle]');
      return Boolean(subscriptionToggle && subscriptionToggle.checked);
    }

    _getCopySelectedCartItem(card = null) {
      if (!this._isCopySubscriptionPdp()) return null;

      const planIndex = this._getSelectedPlanIndex();
      const variantAttrName = `data-copy-plan-${planIndex}-variant-id`;
      const selectedVariantId = this._getSafeId(this.getAttribute(variantAttrName));

      if (!selectedVariantId) {
        console.error('[mpv1-rpp] Copy PDP selected variant ID missing.', {
          planIndex,
          variantAttrName,
          value: this.getAttribute(variantAttrName)
        });

        return null;
      }

      let quantity = 1;

      if (card && card.dataset.qty) {
        const cardQty = parseInt(card.dataset.qty, 10);
        if (Number.isFinite(cardQty) && cardQty > 0) {
          quantity = 1;
        }
      }

      return {
        id: selectedVariantId,
        quantity
      };
    }

    _getSellingPlanId() {
      if (this._isCopySubscriptionPdp()) {
        if (!this._wantsCopySubscription()) return '';

        const sellingPlanId = this._getSafeId(this.getAttribute('data-copy-selling-plan-id'));
        return sellingPlanId ? String(sellingPlanId) : '';
      }

      const input = this.querySelector('[data-selling-plan-new-id]');
      return input ? input.value : '';
    }

    _getCartItemFromUrl(url) {
      if (!url || url === '#') return null;

      let parsedUrl;

      try {
        parsedUrl = new URL(url, window.location.origin);
      } catch (error) {
        console.error('[mpv1-rpp] Invalid ATC URL:', url);
        return null;
      }

      if (parsedUrl.pathname.indexOf('/cart/') === -1) return null;

      const cartPart = parsedUrl.pathname.split('/cart/')[1];
      if (!cartPart) return null;

      const firstItem = cartPart.split(',')[0];
      const cleanItem = firstItem.split('?')[0];
      const parts = cleanItem.split(':');

      const variantId = parseInt(parts[0], 10);
      const quantity = parseInt(parts[1] || '1', 10);

      if (!variantId) return null;

      return {
        id: variantId,
        quantity: quantity || 1
      };
    }

    _showError(message) {
      if (!this.errorEl) return;

      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    _clearError() {
      if (!this.errorEl) return;

      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    async _handleSubscriptionAddToCart() {
      const checked = this.querySelector('.mpv1-rpp__radio:checked');

      if (!checked) {
        console.error('[mpv1-rpp] No selected radio found.');
        this._showError('Please select a supply option.');
        return;
      }

      const card = this._getCardFromRadio(checked);

      if (!card) {
        console.error('[mpv1-rpp] Selected card not found.');
        this._showError('Selected plan not found.');
        return;
      }

      this._clearError();

      const checkoutUrl =
        card.dataset.checkoutUrl ||
        this.atcBtn?.dataset.selectedCheckoutUrl ||
        '';

      const selectedItem = this._isCopySubscriptionPdp()
        ? this._getCopySelectedCartItem(card)
        : this._getCartItemFromUrl(checkoutUrl);

      if (!selectedItem) {
        console.error('[mpv1-rpp] Selected item not found.', {
          isCopySubscriptionPdp: this._isCopySubscriptionPdp(),
          selectedPlanIndex: this._getSelectedPlanIndex(),
          checkoutUrl
        });

        this._showError(
          this._isCopySubscriptionPdp()
            ? 'Please add the selected plan variant ID in Customize settings.'
            : 'Selected item not found from URL.'
        );

        return;
      }

      const sellingPlanId = this._getSellingPlanId();

      if (this._isCopySubscriptionPdp() && this._wantsCopySubscription() && !sellingPlanId) {
        console.error('[mpv1-rpp] Copy PDP selling plan ID missing.');
        this._showError('Please add the selling plan ID in Customize settings.');
        return;
      }

      const cartItem = {
        id: Number(selectedItem.id),
        quantity: Number(selectedItem.quantity || 1),
        properties: {}
      };

      if (sellingPlanId) {
        cartItem.selling_plan = Number(sellingPlanId);
      }

      if (card.dataset.supplyPlan) {
        cartItem.properties['_supply_plan'] = card.dataset.supplyPlan;
      }

      if (card.dataset.rebillDays) {
        cartItem.properties['_rebill_days'] = card.dataset.rebillDays;
      }

      if (Object.keys(cartItem.properties).length === 0) {
        delete cartItem.properties;
      }

      this.atcBtn.classList.add('is-loading');
      this.atcBtn.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [cartItem]
          })
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          console.error('[mpv1-rpp] Cart add error:', data);
          throw new Error(data.description || data.message || 'Could not add to cart');
        }

        document.dispatchEvent(new CustomEvent('cart:item:added', {
          bubbles: true,
          detail: {
            items: data.items || data,
            variantId: cartItem.id,
            quantity: cartItem.quantity,
            sellingPlanId: cartItem.selling_plan || '',
            isCopySubscriptionPdp: this._isCopySubscriptionPdp()
          }
        }));

        this.atcBtn.classList.remove('is-loading');
        this.atcBtn.setAttribute('aria-busy', 'false');

        if (typeof window.updateCartDrawer === 'function') {
          window.updateCartDrawer();
        } else {
          window.location.href = '/cart';
        }
      } catch (error) {
        console.error('[mpv1-rpp] Subscription add error:', error);

        this.atcBtn.classList.remove('is-loading');
        this.atcBtn.setAttribute('aria-busy', 'false');
        this._showError(error.message || 'Something went wrong. Please try again.');
      }
    }

    _parseMoney(str) {
      if (!str) return 0;

      const cleaned = str.replace(/[^0-9.,]/g, '');
      if (!cleaned) return 0;

      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');

      if (lastComma > lastDot) {
        return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
      }

      return parseFloat(cleaned.replace(/,/g, '')) || 0;
    }

    _formatMoney(amount, referenceStr) {
      const symbol = (referenceStr.match(/^[^\d\s]+/) || ['$'])[0];
      return symbol + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2));
    }
  }

  customElements.define('mpv1-pack-selector-resilia', Mpv1PackSelectorResilia);
}

// Accordion 

(function () {
  if (window.__mpv1AccordionFallbackInit) return;
  window.__mpv1AccordionFallbackInit = true;

  function getAccordion(trigger) {
    return trigger.closest('mpv1-accordion') || trigger.closest('.mpv1-accordion');
  }

  function initAccordion(accordion) {
    if (!accordion || accordion.dataset.accordionReady === 'true') return;

    const trigger = accordion.querySelector('.mpv1-accordion__trigger');
    const panel = accordion.querySelector('.mpv1-accordion__panel');

    if (!trigger || !panel) return;

    accordion.dataset.accordionReady = 'true';

    if (!panel.id) {
      panel.id = 'mpv1-accordion-panel-' + Math.random().toString(36).slice(2);
    }

    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', accordion.classList.contains('is-open') ? 'true' : 'false');

    panel.style.overflow = 'hidden';
    panel.style.transition = 'max-height 0.3s ease';

    if (accordion.classList.contains('is-open')) {
      panel.style.maxHeight = panel.scrollHeight + 'px';
      accordion.style.setProperty('--mpv1-accordion-height', panel.scrollHeight + 'px');
    } else {
      panel.style.maxHeight = '0px';
      accordion.style.removeProperty('--mpv1-accordion-height');
    }
  }

  function toggleAccordion(accordion) {
    const trigger = accordion.querySelector('.mpv1-accordion__trigger');
    const panel = accordion.querySelector('.mpv1-accordion__panel');
    const icon = accordion.querySelector('.mpv1-accordion__icon');

    if (!trigger || !panel) return;

    const isOpen = accordion.classList.contains('is-open');

    if (isOpen) {
      accordion.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.maxHeight = '0px';
      accordion.style.removeProperty('--mpv1-accordion-height');

      if (icon && icon.classList.contains('mpv1-accordion__icon--plus')) {
        icon.textContent = '+';
      }
    } else {
      accordion.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');

      const height = panel.scrollHeight;
      panel.style.maxHeight = height + 'px';
      accordion.style.setProperty('--mpv1-accordion-height', height + 'px');

      if (icon && icon.classList.contains('mpv1-accordion__icon--plus')) {
        icon.textContent = '−';
      }
    }
  }

  document.querySelectorAll('mpv1-accordion, .mpv1-accordion').forEach(initAccordion);

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('.mpv1-accordion__trigger');
    if (!trigger) return;

    const accordion = getAccordion(trigger);
    if (!accordion) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    initAccordion(accordion);
    toggleAccordion(accordion);
  }, true);

  document.addEventListener('keydown', function (event) {
    const trigger = event.target.closest('.mpv1-accordion__trigger');
    if (!trigger) return;

    if (event.key !== 'Enter' && event.key !== ' ') return;

    const accordion = getAccordion(trigger);
    if (!accordion) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    initAccordion(accordion);
    toggleAccordion(accordion);
  }, true);
})();